import React, { useState, useMemo, useEffect } from 'react';
import { Trash2, Users, GitMerge } from 'lucide-react';
import Pagination from './Pagination'; // Adjust path if necessary

const DuplicateRecordsTable = ({ 
    records, 
    collectors, 
    filterCollector, 
    setFilterCollector, 
    searchQuery,
    setSearchQuery,
    onRecordSelect, 
    loading,
    selectedRecords,
    onSelectionChange,
    onSelectAll,
    onDeleteSelected,
    onReassign,
    onMergeSelected,
    userRole
}) => {
    const [reassignCollector, setReassignCollector] = useState('');
    
    // --- PAGINATION STATE ---
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(25);

    // --- CLIENT-SIDE FILTERING LOGIC ---
    const filteredRecords = useMemo(() => {
        if (!records) return [];

        return records.filter(record => {
            // 1. Filter by Collector
            if (filterCollector) {
                const recordCollectorId = record.assignedCollector?._id || 'unassigned';
                if (filterCollector === 'unassigned') {
                    if (record.assignedCollector) return false;
                } else {
                    if (recordCollectorId !== filterCollector) return false;
                }
            }

            // 2. Filter by Search Query
            if (searchQuery) {
                const query = searchQuery.toLowerCase();
                const provider = record.provider?.toLowerCase() || '';
                const ptName = record.ptName?.toLowerCase() || '';
                const caseStatus = record.caseStatus?.toLowerCase() || '';
                const adjNumbers = record.adjNumber?.map(a => a.value.toLowerCase()).join(' ') || '';
                
                if (
                    !provider.includes(query) && 
                    !ptName.includes(query) && 
                    !adjNumbers.includes(query) &&
                    !caseStatus.includes(query)
                ) {
                    return false;
                }
            }
            return true;
        });
    }, [records, filterCollector, searchQuery]);

    // --- PAGINATION LOGIC ---
    // Calculate the records to display for the current page
    const paginatedRecords = useMemo(() => {
        const start = (page - 1) * limit;
        return filteredRecords.slice(start, start + limit);
    }, [filteredRecords, page, limit]);

    // Reset to page 1 if filters change
    useEffect(() => {
        setPage(1);
    }, [filterCollector, searchQuery, records]);

    const allVisibleSelected = filteredRecords.length > 0 && filteredRecords.every(r => selectedRecords.includes(r._id));

    const handleReassign = () => {
        if (reassignCollector) {
            onReassign(reassignCollector);
            setReassignCollector('');
        }
    };
    
    const normalizedUserRole = (userRole || '').toLowerCase().replace(/[\s-]/g, '_');
    const canBulkAction = normalizedUserRole === 'admin' || normalizedUserRole === 'super_admin';

    return (
        <div className="bg-white rounded-lg shadow-md p-6 overflow-x-auto">
            <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
              <div className="flex items-center gap-4 flex-wrap">
                <h2 className="text-2xl font-bold text-gray-800">Duplicate Records</h2>
                {canBulkAction && selectedRecords && selectedRecords.length > 0 && (
                    <>
                        <button
                            onClick={onMergeSelected}
                            disabled={!selectedRecords || selectedRecords.length < 2}
                            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md transition-colors disabled:bg-gray-400"
                            title={selectedRecords.length < 2 ? 'Select at least 2 records to merge' : 'Merge selected duplicates'}
                        >
                            <GitMerge className="w-4 h-4" />
                            <span>Merge ({selectedRecords.length})</span>
                        </button>

                        <button
                            onClick={onDeleteSelected}
                            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-md transition-colors"
                        >
                            <Trash2 className="w-4 h-4" />
                            <span>Delete ({selectedRecords.length})</span>
                        </button>
                        <div className="flex items-center gap-2">
                            <select
                                value={reassignCollector}
                                onChange={(e) => setReassignCollector(e.target.value)}
                                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                            >
                                <option value="">Reassign to...</option>
                                {collectors.map(c => <option key={c._id} value={c._id}>{c.username}</option>)}
                            </select>
                            <button
                                onClick={handleReassign}
                                disabled={!reassignCollector}
                                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition-colors disabled:bg-gray-400"
                            >
                                <Users className="w-4 h-4" />
                                <span>Reassign</span>
                            </button>
                        </div>
                    </>
                )}
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                  <div>
                      <label htmlFor="search-records" className="sr-only">Search Records</label>
                      <input
                          type="text"
                          id="search-records"
                          placeholder="Search Provider, Patient..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                      />
                  </div>
                  <div>
                      <label htmlFor="filter-collector" className="sr-only">Filter by Collector</label>
                      <select
                          id="filter-collector"
                          value={filterCollector}
                          onChange={(e) => setFilterCollector(e.target.value)}
                          className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                      >
                          <option value="">All Collectors</option>
                          {collectors.map(c => <option key={c._id} value={c._id}>{c.username}</option>)}
                      </select>
                  </div>
              </div>
              
            </div>

            <div className="mb-4 text-sm text-gray-500 italic">
                * Showing records with matching <strong>Provider</strong>, <strong>Patient Name</strong>, and <strong>Adj Number</strong>.
            </div>

            {loading ? <p>Loading records...</p> : (
            <>
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left">
                                <input
                                    type="checkbox"
                                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                    onChange={onSelectAll}
                                    checked={allVisibleSelected}
                                    disabled={!filteredRecords || filteredRecords.length === 0}
                                />
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provider Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adj Number</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bill</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Case Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned Collector</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedRecords && paginatedRecords.length > 0 ? (
                        paginatedRecords.map((record) => (
                          <tr key={record._id} className={selectedRecords && selectedRecords.includes(record._id) ? 'bg-indigo-50' : ''}>
                            <td className="px-6 py-4 whitespace-nowrap">
                               <input
                                    type="checkbox"
                                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                    checked={selectedRecords ? selectedRecords.includes(record._id) : false}
                                    onChange={() => onSelectionChange(record._id)}
                                />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">{record.provider}</td>
                            <td className="px-6 py-4 whitespace-nowrap">{record.ptName}</td>
                            {/* Showing all ADJ numbers so you can see why it's a duplicate, but plain text style */}
                            <td className="px-6 py-4 whitespace-nowrap">{record?.adjNumber?.map(a => a.value).join(', ') || 'N/A'}</td>
                            <td className="px-6 py-4 whitespace-nowrap">${record.bill?.toLocaleString() || 'N/A'}</td>
                            <td className="px-6 py-4 whitespace-nowrap">{record.caseStatus || 'N/A'}</td>
                            <td className="px-6 py-4 whitespace-nowrap">{record.assignedCollector?.username || 'Unassigned'}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <button onClick={() => onRecordSelect(record)} className="text-indigo-600 hover:text-indigo-900 font-semibold">Details</button>
                            </td>
                          </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="8" className="text-center py-4">No duplicate records found.</td>
                        </tr>
                    )}
                    </tbody>
                </table>
                
                {/* Pagination Component */}
                {filteredRecords.length > 0 && (
                    <Pagination 
                        page={page} 
                        setPage={setPage} 
                        limit={limit} 
                        setLimit={setLimit} 
                        total={filteredRecords.length} 
                    />
                )}
            </>
            )}
        </div>
    );
};

export default DuplicateRecordsTable;